import { hideModal, modalBodyType } from '@/redux/navigationSlice';
import { selectModalData } from '@/redux/selectors';
import { Grid, Typography } from '@mui/material';
import { Modal } from '@repo/ui';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Lazy-load modal body components to reduce initial bundle
const CloseSessionBodyLazy = dynamic(
  () => import('./closeSessionBody').then((mod) => ({ default: mod.CloseSessionBody })),
  {
    loading: () => null,
  },
);

const CodeSentLazy = dynamic(() => import('./codeSent').then((mod) => ({ default: mod.CodeSent })), {
  loading: () => null,
});

const EmailSentLazy = dynamic(() => import('./emailSent').then((mod) => ({ default: mod.EmailSent })), {
  loading: () => null,
});

const bodySelector = (body: modalBodyType) => {
  switch (body) {
    case 'smsSent':
      return <CodeSentLazy />;
    case 'closeSession':
      return <CloseSessionBodyLazy />;
    case 'emailSent':
      return <EmailSentLazy />;
    default:
      return <></>;
  }
};

/**
 * Renders a Modal component with title and body based on redux.
 *
 * @return {React.JSX.Element} The rendered Modal component.
 */
export const CustomerModal = () => {
  const modalData = useSelector(selectModalData);
  const dispatch = useDispatch();
  const { open, body, title, onClose } = modalData;
  const BodyComponent = bodySelector(body);

  // Memoized close handler to prevent re-creation on every render
  const handleClose = useCallback(() => {
    dispatch(hideModal());
    onClose?.();
  }, [dispatch, onClose]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 6 },
        }}
      >
        <Typography variant="h1">{title}</Typography>
        {BodyComponent}
      </Grid>
    </Modal>
  );
};
